import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { PersonIcon } from '../shared/Icons';

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .name-input-wrap {
    position: relative;
    display: block;
    width: 100%;
  }

  /* Centre off the wrapper rather than flex-aligning, so the icon stays
     vertically centred at any themed input height. */
  .name-input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: #9ca3af;
    pointer-events: none;
    z-index: 1;
  }

  .name-input {
    width: 100%;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  .name-input.with-icon {
    padding-left: 38px;
  }

  /* Border width must not change on focus: a 1px -> 2px swap reflows the text
     box and makes the icon/text drift out of alignment. */
  .name-input:focus {
    border-color: #0069ff;
  }

  &.has-error .name-input {
    border-color: #d32f2f;
  }

  .name-input-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 3px;
  }
`;

interface NameInputData {
  field_name?: string;
  required?: boolean;
  value?: string;
  showIcon?: boolean;
  placeholder?: string;
}

interface NameInputProps {
  data: NameInputData;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  /**
   * false on the builder canvas, true in the rendered form — the library sets
   * this, and it is the only reliable signal for "is this a live form?".
   */
  mutable?: boolean;
}

const NameInput = React.forwardRef<HTMLInputElement, NameInputProps>(
  (props, ref) => {
    const {
      field_name,
      required,
      value: initialValue,
      showIcon = true,
      placeholder,
    } = props.data || {};

    const [value, setValue] = useState<string>(initialValue || '');
    const [touched, setTouched] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    // Only validate in the live form — `mutable` is false on the builder
    // canvas, where a "required" error is noise.
    const showError = props.mutable !== false && touched && required && !value;

    const emitChange = (next: string) => {
      setValue(next);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: next });
      }
    };

    return (
      <StyledWrapper className={showError ? 'has-error' : ''}>
        <div className="name-input-wrap ahd-icon-input-wrap">
          {showIcon && (
            <span className="name-input-icon ahd-icon-input-icon">
              <PersonIcon size={18} />
            </span>
          )}
          <input
            className={`name-input${showIcon ? ' with-icon' : ''}`}
            type="text"
            name={field_name}
            placeholder={placeholder || ''}
            value={value}
            disabled={props.read_only}
            onChange={(e) => emitChange(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </div>
        {showError && <div className="name-input-error-text">This field is required.</div>}
        <input ref={hiddenInputRef} type="hidden" name={field_name} value={value} />
      </StyledWrapper>
    );
  },
);

NameInput.displayName = 'NameInput';

export default NameInput;
